import { ProjectionCode } from './leaflet/projection.model';

const northLat = 55;
const southLat = -55;

// https://en.wikipedia.org/wiki/Well-known_text_representation_of_geometry
export function GeoJSONToWKT(fc: GeoJSON.FeatureCollection): string {
  const geoms = [];

  for (const feature of fc.features) {
    switch (feature?.geometry?.type) {
      case 'Point': {
        geoms.push('POINT(' + feature?.geometry?.coordinates.join(' ') + ')');
        break;
      }
      case 'LineString': {
        let linestring = '';
        for (const [i, point] of feature.geometry.coordinates.entries()) {
          if (i !== 0) {
            linestring += ', ';
          }
          linestring += point.join(' ');
        }
        geoms.push('LINESTRING(' + linestring + ')');
        break;
      }
      case 'Polygon': {
        let polygon = '';
        for (const [i, point] of feature.geometry.coordinates[0].entries()) {
          if (i !== 0) {
            polygon += ', ';
          }
          polygon += point.join(' ');
        }
        geoms.push('POLYGON((' + polygon + '))');
        break;
      }
    }
  }

  if (geoms.length > 0) {
    return 'GEOMETRYCOLLECTION(' + geoms.join(',') + ')';
  }
  return '';
}

export function WKTToGeoJSON(geom: string | undefined): GeoJSON.FeatureCollection {
  if (!geom) {
    return { type: 'FeatureCollection', features: [] };
  }

  const featList: GeoJSON.Feature[] = [];

  // Strip the wrapping GEOMETRYCOLLECTION string.
  geom = geom.slice(19);
  geom = geom.slice(-geom.length, -1);

  const featStrings = geom.split('),');
  for (const [i, f] of featStrings.entries()) {
    if (i < featStrings.length - 1) {
      featStrings[i] = f + ')';
    }
  }

  // SUPPORTED SHAPES
  const pnt = 'POINT(';
  const line = 'LINESTRING(';
  const poly = 'POLYGON(';

  for (const f of featStrings) {
    for (const shape of [pnt, line, poly]) {
      if (f.substring(0, shape.length) === shape) {
        let val = f.substring(shape.length);
        val = val.slice(-val.length, -1);
        switch (shape) {
          case pnt:
            featList.push({
              type: 'Feature',
              properties: {
                projection: projFromPair(pointCoords(val)),
              },
              geometry: {
                coordinates: pointCoords(val),
                type: 'Point',
              },
            });
            break;
          case line:
            featList.push({
              type: 'Feature',
              properties: {
                projection: projFromSet(multiCoords(val)),
              },
              geometry: {
                coordinates: multiCoords(val),
                type: 'LineString',
              },
            });
            break;
          case poly:
            // Strip the extra wrapper braces.
            val = val.substring(1);
            val = val.slice(-val.length, -1);

            featList.push({
              type: 'Feature',
              properties: {
                projection: projFromSet(multiCoords(val)),
              },
              geometry: {
                coordinates: [multiCoords(val)],
                type: 'Polygon',
              },
            });
            break;
        }
      }
    }
  }

  return { type: 'FeatureCollection', features: featList };
}

export function GeoJSONToCoordinates(geoj: GeoJSON.FeatureCollection): number[][] {
  const coordinates = [];

  // Loop over all features and look for those that are POINT geometries
  for (const feature of geoj.features) {
    if (feature.geometry.type === 'Point') {
      if (feature.geometry.coordinates.length >= 2) {
        coordinates.push(feature.geometry.coordinates);
      }
    }
  }

  return coordinates;
}

function pointCoords(value: string): number[] {
  if (value === '') {
    return [];
  }

  const coordinates: number[] = [];
  for (const c of value.split(' ')) {
    coordinates.push(parseFloat(c));
  }

  return coordinates;
}

function multiCoords(value: string): number[][] {
  if (value === '') {
    return [];
  }

  const coordinates: number[][] = [];
  for (const p of value.split(', ')) {
    const pair = [];
    for (const c of p.split(' ')) {
      pair.push(parseFloat(c));
    }
    coordinates.push(pair);
  }

  return coordinates;
}

// Figure out which projection to use for a lat/lng pair.
function projFromPair(lngLat: number[]): string {
  const proj = ProjectionCode.Mercator;
  if (lngLat.length === 0) {
    return proj;
  }

  if (lngLat[1] > southLat && lngLat[1] >= northLat) {
    return ProjectionCode.Arctic;
  }

  if (lngLat[1] < northLat && lngLat[1] <= southLat) {
    return ProjectionCode.Antarctic;
  }

  return proj;
}

// Figure out which projection to use for a lat/lng set.
function projFromSet(coords: number[][]): string {
  // PROJECTIONS
  let isArctic = false;
  let isAntarctic = false;

  let proj = ProjectionCode.Mercator;
  if (coords.length === 0) {
    return proj;
  }

  for (const lngLat of coords) {
    if (!isArctic) {
      isArctic = lngLat[1] > southLat && lngLat[1] >= northLat;
    }
    if (!isAntarctic) {
      isAntarctic = lngLat[1] < northLat && lngLat[1] <= southLat;
    }
  }

  if (isArctic && !isAntarctic) {
    proj = ProjectionCode.Arctic;
  } else if (isAntarctic && !isArctic) {
    proj = ProjectionCode.Antarctic;
  }

  return proj;
}

// Add projection by latitude to a GeoJSON Feature of type point
export function addProjection(f: GeoJSON.Feature): GeoJSON.Feature {
  if (f.geometry.type === 'Point') {
    const props = { ...f.properties };
    props['projection'] = projFromPair((f.geometry as GeoJSON.Point).coordinates);
    return { ...f, properties: props };
  }
  return f;
}
